import { useState } from 'react'
import { isRole, roleHasCapability } from '@qim/shared'
import { ApiError, fetchRoleBoundaries } from '../api.js'
import { pathFor } from '../routing.js'
import type { RoleBoundariesView } from '../api.js'
import type { BoundaryCoverage } from '@qim/shared'
import type { JSX } from 'react'

/**
 * "Permissions boundaries", on an account's Onboarding tab (IM-194).
 *
 * Whether the account requires each deployment's boundary, which instances
 * still have none recorded, and — only once there are none — the access
 * template offered with `DeployRoleBoundary=required`. Turning it on earlier
 * would refuse the next apply of every unbounded instance, which is why the
 * API also refuses to serve that template until then.
 *
 * The step itself comes with the onboarding read and costs no call; whether
 * each instance's roles *actually* carry its boundary is asked of IAM on
 * request, a `GetRole` per role.
 */
export const BoundariesStep = ({
  baseUrl,
  tenantId,
  accountKey,
  coverage,
  role,
  requiredTemplate,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly coverage: BoundaryCoverage
  readonly role: string
  /** The access template's download, requiring boundaries — `account.tsx`'s own button. */
  readonly requiredTemplate: JSX.Element
}): JSX.Element => {
  const [roles, setRoles] = useState<RoleBoundariesView | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const canHandOver = isRole(role) && roleHasCapability(role, 'account:onboard')

  const check = async (): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    try {
      setRoles(await fetchRoleBoundaries(baseUrl, tenantId, accountKey))
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 403
          ? 'This role cannot read the account’s roles (account:read).'
          : 'Could not read the account’s roles just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  // A word and a shape first, colour third, as everywhere in this console.
  const state =
    coverage.mode === 'required'
      ? { word: 'Required', mark: '✓' }
      : coverage.canRequire
        ? { word: 'Ready to require', mark: '●' }
        : coverage.mode === 'unknown'
          ? { word: 'Unknown', mark: '?' }
          : { word: 'Not required', mark: '○' }

  return (
    <section className="panel">
      <h2>Permissions boundaries</h2>
      <p>
        <strong>
          <span aria-hidden="true">{state.mark}</span> <span>{state.word}</span>.
        </strong>{' '}
        {coverage.detail}
      </p>

      {coverage.unbounded.length === 0 ? null : (
        <ul>
          {coverage.unbounded.map((id) => (
            <li key={id}>
              <a href={pathFor({ screen: 'deployment', tenantId, deploymentId: id })}>{id}</a> — no
              boundary recorded. Set one up on its screen.
            </li>
          ))}
        </ul>
      )}

      {coverage.canRequire && canHandOver ? requiredTemplate : null}

      <div className="actions">
        <button type="button" className="ghost" onClick={() => void check()} disabled={busy}>
          {busy ? 'Reading the roles…' : 'Check every instance’s roles'}
        </button>
        {failure === undefined ? null : <span className="failure">{failure}</span>}
      </div>

      {roles === undefined ? null : roles.state === 'unreadable' ? (
        <p className="failure">Could not read the account’s roles: {roles.reason}</p>
      ) : roles.instances.length === 0 ? (
        <p className="muted">No instance here has roles to check.</p>
      ) : (
        <ul>
          {roles.instances.map((instance) => (
            <li key={instance.deploymentId}>
              <strong>{instance.deploymentId}</strong>:{' '}
              {instance.unbounded.length === 0
                ? `all ${String(instance.roles)} roles carry its boundary.`
                : `${String(instance.unbounded.length)} of ${String(instance.roles)} roles do not carry its boundary (${instance.unbounded
                    .map((r) => r.name)
                    .join(', ')}).${
                    instance.recorded
                      ? ' Its boundary is recorded, so the next apply bounds them in place.'
                      : ''
                  }`}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
